import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { boardHistoryFilterChanged, boardHistoryTabChanged } from "@/analytics/events/history";
import { trackEvent } from "@/analytics/track";
import { pauseSubscriptions } from "@/backend/read/BackendReader";
import type { Card } from "@/model/card";
import type { ChangeTypeItem } from "@/model/change";
import { serverStickyActivityKeys } from "@/model/change";
import type { Iteration } from "@/model/session";
import type { StickyType } from "@/model/stickyType";
import { toKebabCase } from "@/utils/text/text";
import { asReadonly, asSemiReadonly } from "@/utils/vue";

import { useBoardStore } from "./board";
import { useCardStore } from "./card";
import { dateFilterValues, defaultDate } from "./helpers/activityDateFilters";
import { resetAllCardMarks } from "./helpers/mark";
import { useSessionStore } from "./session";

export interface EventFilter {
  property: string;
  values: unknown[];
}

export type BoardHistoryTab = "log" | "compare" | "visualize";

const defaultTab = "log";

export const useActivityStore = defineStore("activity", () => {
  const card = ref<Card | null>(null);
  const board = ref(false);
  const tab = ref<BoardHistoryTab>(defaultTab);
  const selectedIterationIds = ref<Set<number>>(new Set());
  const selectedStickyTypeIds = ref<Set<string>>(new Set());
  const selectedDate = ref(defaultDate());
  const selectedChangeTypes = ref<ChangeTypeItem[]>([]);

  const allFilters = computed(() => {
    // Fetch the server-version of change types, and add the unlink/unmirror
    // types if the reciprocal is already in the list
    // (for the user, this means that selecting 'link' changes will also show unlink changes)
    const changeTypes = selectedChangeTypes.value.map((t) => serverStickyActivityKeys[t.id]);
    if (changeTypes.includes("link")) {
      changeTypes.push("unlink");
    }
    if (changeTypes.includes("mirror")) {
      changeTypes.push("unmirror");
    }

    return {
      board: board.value,
      date: selectedDate.value,
      iterations: [...selectedIterationIds.value],
      types: [...selectedStickyTypeIds.value],
      changeTypes,
    };
  });
  const serverFilters = computed(() => {
    const filters = [];
    if (selectedIterationIds.value.size) {
      filters.push({ property: "iteration_number", values: [...selectedIterationIds.value] });
    }
    if (selectedStickyTypeIds.value.size) {
      filters.push({ property: "type_id", values: [...selectedStickyTypeIds.value] });
    }

    return filters;
  });

  const dateFilterOptions = computed(() => dateFilterValues.filter((i) => i.show()));
  const allIterations = computed(() => useSessionStore().iterations);
  const selectedIterations = computed(() =>
    allIterations.value.filter((iteration) => selectedIterationIds.value.has(iteration.id)),
  );

  const selectedStickyTypes = computed(() =>
    useBoardStore().possibleStickyTypes.filter((type) => selectedStickyTypeIds.value.has(type.id)),
  );

  function toggleCard(id: string) {
    if (card.value?.id === id) return inactivate();

    card.value = useCardStore().cards[id];
    inactivateBoardHistory();
  }

  function toggleBoard() {
    if (board.value) return inactivate();

    board.value = true;
    inactivateCardHistory();
  }

  function inactivate() {
    inactivateCardHistory();
    inactivateBoardHistory();
  }

  function inactivateBoardHistory() {
    if (board.value) {
      board.value = false;
      resetFilters();
      doSetTab(defaultTab);
    }
  }

  function inactivateCardHistory() {
    if (card.value) {
      card.value = null;
    }
  }

  function setTab(boardTab: BoardHistoryTab) {
    doSetTab(boardTab);
    resetFilters();
    trackEvent(boardHistoryTabChanged(tab.value));
  }

  function doSetTab(boardTab: BoardHistoryTab) {
    tab.value = boardTab;
    pauseSubscriptions(tab.value === "visualize");
  }

  function resetFilters() {
    selectedIterationIds.value = new Set();
    selectedStickyTypeIds.value = new Set();
    selectedDate.value = defaultDate();
    selectedChangeTypes.value = [];
    resetAllCardMarks(useBoardStore().currentBoard());
  }

  function selectIterations(iterations: Iteration[]) {
    selectedIterationIds.value = new Set(iterations.map((i) => i.id));
    trackEvent(
      boardHistoryFilterChanged({
        filterName: "iteration",
        selectedValues: iterations.map((i) => i.id.toString()),
        tab: tab.value,
      }),
    );
  }

  function selectStickyTypes(stickyTypes: StickyType[]) {
    selectedStickyTypeIds.value = new Set(stickyTypes.map((t) => t.id));
    trackEvent(
      boardHistoryFilterChanged({
        filterName: "sticky-type",
        selectedValues: stickyTypes.map((i) => i.id),
        tab: tab.value,
      }),
    );
  }

  function selectChangeTypes(changeTypes: ChangeTypeItem[]) {
    selectedChangeTypes.value = changeTypes;
    trackEvent(
      boardHistoryFilterChanged({
        filterName: "change-type",
        selectedValues: changeTypes.map((i) => toKebabCase(i.id)),
        tab: tab.value,
      }),
    );
  }

  return {
    card: asSemiReadonly(card),
    board: asReadonly(board),
    tab: asReadonly(tab),
    selectedIterations: asReadonly(selectedIterations),
    selectedStickyTypes: asReadonly(selectedStickyTypes),
    selectedDate: asReadonly(selectedDate),
    selectedChangeTypes: asReadonly(selectedChangeTypes),

    active: computed(() => !!card.value || board.value),
    isCardActivityOpen: computed(() => !!card.value),
    isBoardActivityOpen: computed(() => board.value),

    setTab,
    selectIterations,
    selectStickyTypes,
    selectChangeTypes,

    allFilters,
    serverFilters,
    dateFilterOptions,
    allIterations,

    inactivate,
    toggleCard,
    toggleBoard,
  };
});
